import React from 'react';
import { Box, Typography, Stack, MenuItem, FormControlLabel, Radio, RadioGroup, Alert } from '@mui/material';
import { 
  Assignment as AssignmentIcon,
  Event as DateIcon,
  Schedule as NoticeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useFormContext, Controller } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import FormSelect from '../../shared/FormSelect';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface LeaseTermsStepProps {
  stepProgress: number;
}

const LeaseTermsStep: React.FC<LeaseTermsStepProps> = ({ stepProgress }) => {
  const { control, watch } = useFormContext();
  const leaseType = watch('terms.type');
  const progress = stepProgress;

  return (
    <StepFormLayout
      title="Lease Terms"
      description="Define the duration and terms of the lease agreement"
      icon={<AssignmentIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={progress}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>📋 Tip:</strong> Clear lease terms protect both landlord and tenant rights and prevent disputes.
        </Typography>
      </Alert>
      
      {/* Lease Type & Duration */}
      <FormSection
        title="Lease Type & Duration"
        icon={<DateIcon color="primary" />}
        tooltip="Choose between fixed-term or month-to-month lease"
        isRequired={true}
        defaultExpanded={true}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Lease Type
            </Typography>
            <Controller
              name="terms.type"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field}>
                  <FormControlLabel 
                    value="month-to-month" 
                    control={<Radio />} 
                    label="Month-to-Month Lease" 
                  />
                  <FormControlLabel 
                    value="fixed" 
                    control={<Radio />} 
                    label="Fixed Term Lease" 
                  />
                </RadioGroup>
              )}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormTextField
              name="terms.startDate"
              control={control}
              label="Lease Start Date"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
            />
            
            {leaseType === 'fixed' && (
              <FormTextField
                name="terms.endDate"
                control={control}
                label="Lease End Date"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
          
          {leaseType === 'fixed' && (
            <>
              <Controller
                name="terms.autoRenewal"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Radio {...field} checked={field.value} />}
                    label="Automatically renew as month-to-month at expiration"
                  />
                )}
              />
              
              <FormTextField
                name="terms.renewalTerms"
                control={control}
                label="Renewal Terms (Optional)"
                multiline
                rows={2}
              />
            </>
          )}
        </Stack>
      </FormSection>
      
      {/* Notice Requirements */}
      <FormSection
        title="Notice Requirements"
        icon={<NoticeIcon color="primary" />}
        tooltip="Required notice periods for termination and rent increases"
        isRequired={false}
        defaultExpanded={false}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <FormSelect
            name="terms.noticeRequired"
            control={control}
            label="Notice Required for Termination"
            required
          >
            <MenuItem value={30}>30 Days</MenuItem>
            <MenuItem value={60}>60 Days</MenuItem>
            <MenuItem value={90}>90 Days</MenuItem>
          </FormSelect>
          
          <FormTextField
            name="terms.rentIncreaseNotice"
            control={control}
            label="Rent Increase Notice (Days)"
            type="number"
            required
          />
        </Box>
      </FormSection>
      
      {/* Additional Terms */}
      <FormSection
        title="Additional Terms"
        icon={<AssignmentIcon color="primary" />}
        tooltip="Move-out requirements and other lease conditions"
        isRequired={false}
        defaultExpanded={false}
      >
        <FormTextField
          name="terms.moveOutRequirements"
          control={control}
          label="Move-Out Requirements (Optional)"
          multiline
          rows={3}
          helperText="Specify any requirements for when the tenant moves out (cleaning, repairs, etc.)"
        />
      </FormSection>
      

      </Box>
    </StepFormLayout>
  );
};

export default LeaseTermsStep; 